import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Combobox } from "@/components/common/Combobox";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadCloud, Download, FileSpreadsheet, Loader2, History, ShieldCheck,
  AlertTriangle, CheckCircle2, Boxes, ScanText, Plus, Pencil, Trash2,
} from "lucide-react";

const BILL_TYPES = ["HIST_PURCHASE", "HIST_SALES"];
const BILL_EXTS = [".pdf", ".jpg", ".jpeg", ".png"];
const MIME = { pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" };

function toB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// Dialog to create the missing master (supplier/buyer/ingredient/product) without leaving the review.
function AddMasterDialog({ open, onClose, kind, initialName, initialGstin, onCreated }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: initialName || "", gstin: initialGstin || "" }); }, [open, initialName, initialGstin]);
  const label = { supplier: "Supplier", buyer: "Buyer", ingredient: "Ingredient", product: "Product" }[kind];
  const endpoint = { supplier: "/suppliers", buyer: "/buyers", ingredient: "/ingredients", product: "/products" }[kind];
  const idKey = { supplier: "supplierId", buyer: "buyerId", ingredient: "ingredientId", product: "productId" }[kind];

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { name: form.name.trim() };
      if (form.gstin) payload.gstin = form.gstin.trim();
      if (kind === "ingredient") payload.unit = form.unit || "kg";
      if (kind === "product") payload.productType = form.productType || "";
      const r = await api.post(endpoint, payload);
      const created = r.data || {};
      onCreated({ _id: created.id || created._id, code: created[idKey], name: created.name || payload.name });
      toast.success(`${label} created (${created[idKey] || ""})`);
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, `Could not create ${label}`)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white sm:max-w-md" data-testid={`hdi-add-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Plus className="h-4 w-4 text-amber-700" /> New {label}</DialogTitle>
          <DialogDescription className="text-xs">Create the missing master record. It will be selected for this bill row automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Name</label>
            <Input data-testid="hdi-add-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          {(kind === "supplier" || kind === "buyer") && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">GSTIN (optional)</label>
              <Input data-testid="hdi-add-gstin" value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
            </div>
          )}
          {kind === "ingredient" && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Unit</label>
              <Select value={form.unit || "kg"} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="hdi-add-unit" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["kg", "g", "ltr", "ml", "pcs", "box", "pkt"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {kind === "product" && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Classification</label>
              <Select value={form.productType || ""} onValueChange={(v) => setForm({ ...form, productType: v })}>
                <SelectTrigger data-testid="hdi-add-ptype" className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="BAKERY">Bakery</SelectItem>
                  <SelectItem value="NON_BAKERY">Non-Bakery</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="hdi-add-cancel" onClick={onClose}>Cancel</Button>
          <Button data-testid="hdi-add-save" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={save}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HistoricalDataImport() {
  const [types, setTypes] = useState([]);
  const [itype, setItype] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);

  // bill-scan state
  const [scan, setScan] = useState(null); // {header, lines[]}
  const [masters, setMasters] = useState({ suppliers: [], buyers: [], ingredients: [], products: [] });
  const [partyDlg, setPartyDlg] = useState(false);
  const [lineDlg, setLineDlg] = useState(-1); // index of line being master-created

  const loadBatches = () => api.get("/historical-import/batches").then((r) => setBatches(r.data || [])).catch(() => {});
  useEffect(() => { api.get("/historical-import/types").then((r) => setTypes(r.data || [])); loadBatches(); }, []);

  const meta = useMemo(() => types.find((t) => t.key === itype), [types, itype]);
  const isBillType = BILL_TYPES.includes(itype);
  const ext = (file?.name || "").split(".").pop()?.toLowerCase() || "";
  const isBillFile = !!MIME[ext]; // purely file-based; type match is validated separately

  // load master lists lazily for the editable scan review
  const loadMasters = async () => {
    if (itype === "HIST_PURCHASE") {
      const [s, i] = await Promise.all([api.get("/suppliers"), api.get("/ingredients")]);
      setMasters((m) => ({ ...m, suppliers: s.data || [], ingredients: i.data || [] }));
    } else if (itype === "HIST_SALES") {
      const [b, p] = await Promise.all([api.get("/buyers"), api.get("/products")]);
      setMasters((m) => ({ ...m, buyers: b.data || [], products: p.data || [] }));
    }
  };

  const downloadTemplate = async () => {
    const r = await api.get("/historical-import/template", { params: { importType: itype } });
    const blob = new Blob([r.data.csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${itype}-template.csv`; a.click();
  };

  const resetOutputs = () => { setPreview(null); setScan(null); };

  const doPreview = async () => {
    if (!file || !itype) return;
    // A scanned bill (PDF/JPG/PNG) only makes sense for Purchase / Sales history
    if (isBillFile && !isBillType) {
      toast.error("Bill scanning is available for 'Historical Purchase Records' and 'Historical Sales Records'. Switch the Import Type, or upload a CSV/Excel for this type.");
      return;
    }
    setBusy(true); setPreview(null); setScan(null);
    try {
      const b64 = await toB64(file);
      if (isBillFile) {
        const r = await api.post("/historical-import/scan", { importType: itype, filename: file.name, contentBase64: b64, mimeType: MIME[ext] });
        setScan({ ...r.data, filename: file.name });
        await loadMasters();
      } else {
        const r = await api.post("/historical-import/preview", { importType: itype, filename: file.name, contentBase64: b64 });
        setPreview(r.data);
      }
    } catch (e) { toast.error(apiErrorMessage(e, isBillFile ? "Scan failed" : "Preview failed")); } finally { setBusy(false); }
  };

  const doCommitCsv = async () => {
    setBusy(true);
    try {
      const b64 = await toB64(file);
      const r = await api.post("/historical-import/commit", { importType: itype, filename: file.name, contentBase64: b64 });
      toast.success(r.data.idempotentReplay ? "This file was already imported — no duplicates created" : `Imported ${r.data.imported} record(s)`);
      resetOutputs(); setFile(null); loadBatches();
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); } finally { setBusy(false); }
  };

  // ---- scan review helpers ----
  const setHdr = (patch) => setScan((s) => ({ ...s, header: { ...s.header, ...patch } }));
  const setLine = (i, patch) => setScan((s) => ({ ...s, lines: s.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const removeLine = (i) => setScan((s) => ({ ...s, lines: s.lines.filter((_, j) => j !== i) }));

  const partyList = itype === "HIST_PURCHASE" ? masters.suppliers : masters.buyers;
  const lineList = itype === "HIST_PURCHASE" ? masters.ingredients : masters.products;
  const partyIdKey = itype === "HIST_PURCHASE" ? "supplierId" : "buyerId";
  const lineIdKey = itype === "HIST_PURCHASE" ? "ingredientId" : "productId";
  const lineKind = itype === "HIST_PURCHASE" ? "ingredient" : "product";

  const scanValid = scan && scan.header.matchedId && scan.lines.length > 0 &&
    scan.lines.every((l) => l.matchedId && parseFloat(l.quantity) > 0);

  const commitScan = async () => {
    setBusy(true);
    try {
      const b64 = await toB64(file);
      const rows = scan.lines.map((l) => {
        const r = { quantity: l.quantity, unit: l.unit, rate: l.rate, billNo: scan.header.billNo, date: scan.header.date };
        r[partyIdKey] = scan.header.matchedId;
        r[lineIdKey] = l.matchedId;
        return r;
      });
      const r = await api.post("/historical-import/commit", { importType: itype, filename: file.name, contentBase64: b64, rows });
      toast.success(r.data.idempotentReplay ? "This bill was already imported — no duplicates created" : `Imported ${r.data.imported} record(s)`);
      resetOutputs(); setFile(null); loadBatches();
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); } finally { setBusy(false); }
  };

  const afterPartyCreated = (rec) => {
    const list = itype === "HIST_PURCHASE" ? "suppliers" : "buyers";
    setMasters((m) => ({ ...m, [list]: [...m[list], { _id: rec._id, [partyIdKey]: rec.code, name: rec.name }] }));
    setHdr({ matchedId: rec._id, matchedName: rec.name });
  };
  const afterLineCreated = (rec) => {
    const list = itype === "HIST_PURCHASE" ? "ingredients" : "products";
    setMasters((m) => ({ ...m, [list]: [...m[list], { _id: rec._id, [lineIdKey]: rec.code, name: rec.name }] }));
    if (lineDlg >= 0) setLine(lineDlg, { matchedId: rec._id, matchedCode: rec.code, matchedName: rec.name, matchStatus: "MATCHED" });
  };

  const numCls = "h-9 w-24 text-right font-mono";
  return (
    <div className="space-y-6" data-testid="historical-import-page">
      <PageHeader title="Historical Data Import" subtitle="Super Admin only · Import historical information without creating sales, purchase or inventory transactions." />

      <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-3 text-sm text-sky-800">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Use this for legacy/historical records or opening balances. Historical imports <strong>do not</strong> create operational transactions (no PO, GRN, QA, Purchase/Sales Bill or Dispatch). Opening-stock imports post an opening-balance ledger entry only. For <strong>Purchase / Sales history</strong> you can upload a <strong>PDF, JPG or PNG bill</strong> — it will be scanned and auto-read for review.</span>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Import Type</label>
            <Select value={itype} onValueChange={(v) => { setItype(v); resetOutputs(); setFile(null); }}>
              <SelectTrigger data-testid="hdi-type" className="h-10"><SelectValue placeholder="Select import type" /></SelectTrigger>
              <SelectContent className="bg-white">
                {types.map((t) => (
                  <SelectItem key={t.key} value={t.key} data-testid={`hdi-type-${t.key}`}>
                    {t.label}{t.affectsInventory ? " · affects inventory" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button data-testid="hdi-template" variant="outline" className="gap-2" disabled={!itype} onClick={downloadTemplate}><Download className="h-4 w-4" /> Template</Button>
            <label className="flex-1">
              <input data-testid="hdi-file" type="file"
                accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png"
                className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); resetOutputs(); }} />
              <span className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-stone-300 px-3 text-sm text-stone-600 hover:border-amber-400">
                {isBillFile ? <ScanText className="h-4 w-4" /> : <FileSpreadsheet className="h-4 w-4" />}
                {file ? file.name : isBillType ? "Choose CSV / Excel / Bill (PDF·JPG·PNG)" : "Choose CSV / Excel (or Bill for Purchase/Sales history)"}
              </span>
            </label>
          </div>
        </div>
        {meta?.affectsInventory && <div className="flex items-center gap-1.5 text-xs text-amber-700"><Boxes className="h-3.5 w-3.5" /> This import creates opening-balance stock ledger entries.</div>}
        {isBillType && <div className="flex items-center gap-1.5 text-xs text-stone-500"><ScanText className="h-3.5 w-3.5" /> Uploading a PDF/JPG/PNG bill scans it and opens an editable review before import.</div>}
        {!isBillType && itype && <div className="flex items-center gap-1.5 text-xs text-stone-400"><FileSpreadsheet className="h-3.5 w-3.5" /> This type imports from CSV/Excel. To scan a bill (PDF/JPG/PNG), use Historical Purchase Records or Historical Sales Records.</div>}
        {file && isBillFile && !isBillType && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="hdi-bill-mismatch">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span><strong>{file.name}</strong> is a bill file (PDF/JPG/PNG) — it can only be scanned as Purchase or Sales history. Switch type:</span>
            <Button size="sm" variant="outline" className="h-7 bg-white text-xs" data-testid="hdi-switch-purchase"
              onClick={() => { setItype("HIST_PURCHASE"); resetOutputs(); }}>
              Historical Purchase Records
            </Button>
            <Button size="sm" variant="outline" className="h-7 bg-white text-xs" data-testid="hdi-switch-sales"
              onClick={() => { setItype("HIST_SALES"); resetOutputs(); }}>
              Historical Sales Records
            </Button>
          </div>
        )}
        <div className="flex justify-end">
          <Button data-testid="hdi-preview" className="gap-2 bg-stone-800 hover:bg-stone-900" disabled={!file || !itype || busy} onClick={doPreview}>
            {busy && !preview && !scan ? <Loader2 className="h-4 w-4 animate-spin" /> : (isBillFile ? <ScanText className="h-4 w-4" /> : <UploadCloud className="h-4 w-4" />)}
            {isBillFile ? "Scan Bill" : "Preview"}
          </Button>
        </div>
      </div>

      {/* ---------- EDITABLE BILL SCAN REVIEW ---------- */}
      {scan && (
        <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm space-y-5" data-testid="hdi-scan-panel">
          <div className="flex items-center gap-2">
            <ScanText className="h-4 w-4 text-amber-700" />
            <h3 className="font-display text-base font-semibold text-stone-900">Scanned Bill — Review &amp; Confirm</h3>
            <span className="text-xs text-stone-400">({scan.filename})</span>
          </div>

          {/* header / party */}
          <div className="grid gap-4 rounded-lg border border-stone-200 bg-stone-50/50 p-4 sm:grid-cols-4">
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">{itype === "HIST_PURCHASE" ? "Supplier" : "Buyer"}</label>
              <div className="flex items-center gap-2">
                <Select value={scan.header.matchedId || ""} onValueChange={(v) => { const it = partyList.find((p) => p._id === v); setHdr({ matchedId: v, matchedName: it?.name || "" }); }}>
                  <SelectTrigger data-testid="hdi-scan-party" className="h-9 bg-white"><SelectValue placeholder={`Match ${itype === "HIST_PURCHASE" ? "supplier" : "buyer"}`} /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {partyList.map((p) => <SelectItem key={p._id} value={p._id}>{p[partyIdKey] || ""} · {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="shrink-0 gap-1" data-testid="hdi-scan-party-add"
                  onClick={() => setPartyDlg(true)}>
                  <Plus className="h-3.5 w-3.5" /> New
                </Button>
              </div>
              <div className="text-[11px] text-stone-500">
                Extracted: <strong>{scan.header.extractedName || "—"}</strong>{scan.header.extractedGstin ? ` · GSTIN ${scan.header.extractedGstin}` : ""}
                {scan.header.matchedId ? <span className="ml-1 text-emerald-700">✓ {scan.header.matchedName}</span> : <span className="ml-1 text-amber-700">⚠ not matched — select or add new</span>}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Bill / Invoice No.</label>
              <Input data-testid="hdi-scan-billno" className="h-9" value={scan.header.billNo} onChange={(e) => setHdr({ billNo: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-stone-500">Date</label>
              <Input data-testid="hdi-scan-date" type="date" className="h-9" value={scan.header.date} onChange={(e) => setHdr({ date: e.target.value })} />
            </div>
          </div>

          {/* lines */}
          <div className="overflow-auto rounded-lg border border-stone-200">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-3 py-2">Bill Item (as read)</th>
                  <th className="px-3 py-2">Matched {itype === "HIST_PURCHASE" ? "Ingredient" : "Product"}</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {scan.lines.map((l, i) => (
                  <tr key={i} data-testid={`hdi-scan-line-${i}`}>
                    <td className="px-3 py-2">
                      <Input data-testid={`hdi-scan-name-${i}`} className="h-9" value={l.extractedName} onChange={(e) => setLine(i, { extractedName: e.target.value })} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <Combobox testId={`hdi-scan-match-${i}`} className={`w-full ${l.matchedId ? "" : "border-amber-400"}`}
                          value={l.matchedId || ""} placeholder="Search &amp; match…"
                          onChange={(v) => { const it = lineList.find((x) => x._id === v); setLine(i, { matchedId: v, matchedCode: it?.[lineIdKey] || "", matchedName: it?.name || "", matchStatus: "MATCHED" }); }}
                          options={lineList.map((x) => ({ value: x._id, label: x.name, hint: x[lineIdKey] || "" }))} />
                        <Button size="sm" variant="ghost" className="shrink-0 px-2" title="Add new" data-testid={`hdi-scan-add-${i}`} onClick={() => setLineDlg(i)}><Plus className="h-4 w-4" /></Button>
                      </div>
                      {l.matchedId ? (
                        <div className="mt-0.5 text-[11px] text-emerald-700">✓ {l.matchedCode} {l.matchedName}</div>
                      ) : l.suggestedId ? (
                        <button type="button" data-testid={`hdi-scan-suggest-${i}`}
                          className="mt-0.5 text-left text-[11px] text-amber-700 hover:underline"
                          onClick={() => setLine(i, { matchedId: l.suggestedId, matchedCode: l.suggestedCode || "", matchedName: l.suggestedName || "", matchStatus: "MATCHED" })}>
                          Did you mean <strong>{l.suggestedName}</strong>? — click to match
                        </button>
                      ) : (
                        <div className="mt-0.5 text-[11px] text-amber-700">⚠ not matched — search or add new</div>
                      )}
                    </td>
                    <td className="px-3 py-2"><Input data-testid={`hdi-scan-qty-${i}`} className={numCls} value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                    <td className="px-3 py-2"><Input data-testid={`hdi-scan-unit-${i}`} className="h-9 w-20" value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} /></td>
                    <td className="px-3 py-2"><Input data-testid={`hdi-scan-rate-${i}`} className={numCls} value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></td>
                    <td className="px-3 py-2"><Button size="sm" variant="ghost" className="text-rose-500" data-testid={`hdi-scan-del-${i}`} onClick={() => removeLine(i)}><Trash2 className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
                {scan.lines.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-stone-400">No line items were read from this bill.</td></tr>}
              </tbody>
            </table>
          </div>

          {!scanValid && (
            <div className="flex items-center gap-2 text-xs text-amber-700"><AlertTriangle className="h-4 w-4" />
              Resolve the {itype === "HIST_PURCHASE" ? "supplier" : "buyer"} and every line match (and a quantity &gt; 0) before importing.
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="hdi-scan-cancel" onClick={() => { resetOutputs(); setFile(null); }}>Discard</Button>
            <Button data-testid="hdi-scan-commit" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !scanValid} onClick={commitScan}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm Import
            </Button>
          </div>
        </div>
      )}

      {/* ---------- CSV / EXCEL PREVIEW ---------- */}
      {preview && (
        <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm space-y-4" data-testid="hdi-preview-panel">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 text-center">
            {[["Total", preview.totalRows, "text-stone-800"], ["Valid", preview.validRows, "text-emerald-700"],
              ["Match req.", preview.matchRequired, "text-amber-700"], ["Errors", preview.errorRows, "text-rose-600"],
              ["Batch", preview.alreadyImported ? "already imported" : "new", "text-stone-500"]].map(([l, v, c]) => (
              <div key={l} className="rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">{l}</div>
                <div className={`font-mono text-base font-bold ${c}`} data-testid={`hdi-count-${l.toLowerCase().replace(/[^a-z]/g,'')}`}>{v}</div>
              </div>
            ))}
          </div>
          <div className="max-h-72 overflow-auto rounded-lg border border-stone-200">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-stone-50 text-left text-[10px] uppercase text-stone-500">
                <tr><th className="px-2 py-1.5">Row</th>{(meta?.cols || []).map((c) => <th key={c} className="px-2 py-1.5">{c}</th>)}<th className="px-2 py-1.5">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {preview.rows.slice(0, 200).map((r) => (
                  <tr key={r._row}>
                    <td className="px-2 py-1.5 text-stone-400">{r._row}</td>
                    {(meta?.cols || []).map((c) => <td key={c} className="px-2 py-1.5">{r[c]}</td>)}
                    <td className="px-2 py-1.5">
                      <span className={r._status === "OK" ? "text-emerald-600" : r._status === "MATCH_REQUIRED" ? "text-amber-700" : "text-rose-600"}>
                        {r._status === "OK" ? "✓" : r._status === "MATCH_REQUIRED" ? "⚠ match" : "✕"} {r._errors?.join("; ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.errorRows > 0 && <div className="flex items-center gap-2 text-xs text-rose-700"><AlertTriangle className="h-4 w-4" /> {preview.errorRows} row(s) have errors and will be skipped.</div>}
          <div className="flex justify-end">
            <Button data-testid="hdi-commit" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy || preview.validRows === 0} onClick={doCommitCsv}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm Import
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="hdi-history">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-800"><History className="h-4 w-4 text-stone-500" /> Import History</div>
        {batches.length === 0 ? <p className="text-sm text-muted-foreground">No imports yet.</p> : (
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase text-stone-500"><tr><th className="py-1.5">Batch</th><th>Type</th><th>File</th><th className="text-right">Imported</th><th>By</th><th>Status</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {batches.map((b) => (
                <tr key={b.id || b._id} data-testid={`hdi-batch-${b.id || b._id}`}>
                  <td className="py-2 font-mono text-xs">{b.id || b._id}</td><td>{b.label}</td><td className="text-stone-500">{b.filename}</td>
                  <td className="text-right font-mono">{b.imported ?? "—"}</td><td className="text-stone-500">{b.importedBy}</td>
                  <td><span className={b.status === "Imported" ? "text-emerald-600" : b.status === "Partially Imported" ? "text-amber-700" : "text-stone-500"}>{b.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* inline master-create dialogs */}
      <AddMasterDialog open={partyDlg} onClose={() => setPartyDlg(false)}
        kind={itype === "HIST_PURCHASE" ? "supplier" : "buyer"}
        initialName={scan?.header.extractedName} initialGstin={scan?.header.extractedGstin}
        onCreated={(rec) => afterPartyCreated(rec)} />
      <AddMasterDialog open={lineDlg >= 0} onClose={() => setLineDlg(-1)}
        kind={lineKind}
        initialName={lineDlg >= 0 ? scan?.lines[lineDlg]?.extractedName : ""}
        onCreated={(rec) => afterLineCreated(rec)} />
    </div>
  );
}
